import { LockOutlined, WarningOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Modal, Space, Typography, message } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../services/api";

const { Title, Paragraph } = Typography;

interface TOTPDisableModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function TOTPDisableModal({
  open,
  onClose,
  onSuccess,
}: TOTPDisableModalProps): JSX.Element {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [verifyCode, setVerifyCode] = useState<string>("");

  const handleDisable = async (): Promise<void> => {
    if (!verifyCode) {
      message.error(t("totpDisable.enterPassword"));
      return;
    }

    try {
      setLoading(true);

      await api.disableTOTP(verifyCode);

      message.success(t("totpDisable.success"));
      onSuccess?.();
      onClose();
      setVerifyCode("");
    } catch (error) {
      console.error("Failed to disable TOTP:", error);
      message.error(error instanceof Error ? error.message : t("totpDisable.invalidPassword"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t("totpDisable.title")}
      open={open}
      onCancel={() => {
        onClose();
        setVerifyCode("");
      }}
      width={500}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            onClose();
            setVerifyCode("");
          }}
        >
          {t("totpDisable.cancel")}
        </Button>,
        <Button
          key="disable"
          type="primary"
          danger
          onClick={handleDisable}
          loading={loading}
          disabled={verifyCode.length !== 6}
          icon={<WarningOutlined />}
        >
          {t("totpDisable.disableButton")}
        </Button>,
      ]}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Alert
          message={t("totpDisable.warning.message")}
          description={t("totpDisable.warning.description")}
          type="warning"
          showIcon
          icon={<WarningOutlined />}
        />

        <div>
          <Title level={4}>{t("totpDisable.confirmTitle")}</Title>
          <Input.Password
            size="large"
            placeholder={t("totpDisable.passwordPlaceholder")}
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value)}
            style={{ fontSize: "16px" }}
            autoFocus
            onPressEnter={handleDisable}
            prefix={<LockOutlined />}
          />
        </div>

        <Paragraph type="secondary">{t("totpDisable.footerNote")}</Paragraph>
      </Space>
    </Modal>
  );
}
